<script setup>
import {computed, inject, onMounted, onUpdated, ref, watch} from "vue";
import trackersCompaniesService from "@/services/trackers-companies.service";
import {getLogo} from "@/helpers/logo";
import {monthsList} from "@/constants/constants";
import {firstDayOfMonth, lastDayOfMonth} from "@/helpers/date";
import TimelineEdit from "@/components/trackers/TimelineEdit.vue";
import {useStore} from "vue2-helpers/vuex";

const store = useStore();

const emit = defineEmits(['timelineChanged']);

const tracker = inject('tracker');
const serverFilters = inject("serverFilters");
const addStartups = inject("addStartups");
const removeStartup = inject("removeCompany");
const companiesListUpdated = inject("companiesListUpdated");
const trackerUpdated = inject("trackerUpdated");
const trackerId = tracker.value.id;
const startupsLoaded = ref(false);
const startups = ref([]);
const months = ref([]);
const startDate = ref(null);
const endDate = ref(null);
const timeline = ref(null);
const timelineEditModal = ref(null);
const startupsInnerBlock = ref(null);
const monthRefs = ref([]);
const initScrolled = ref(false);

const timelineWidth = computed(() => months.value.length < 12 ? ({'width': '100%'}) : ({'width': (90 * months.value.length) + 255 + 'px'}));

watch(trackerUpdated, (value) => {
    if (value) {
        getTimelineList();
    }
});

watch(companiesListUpdated, (value) => {
    if (value) {
        getTimelineList();
    }
});

watch(startups, () => {
    calculateMonthsList();
});

onMounted(() => {
    getTimelineList();
});

onUpdated(() => {
    if (!initScrolled.value && months.value) {
        let index = months.value.findIndex(month => month.isCurrent) - 3;

        if (index >= 0) {
            monthRefs.value[index].scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            initScrolled.value = true;
        }
    }

    let pocBlocks = document.querySelectorAll('.poc-block');
    let width = startupsInnerBlock.value.offsetWidth - 255 + 'px';

    pocBlocks.forEach(block => {
        if (block.classList.contains('poc-block--add')) {
            block.style.width = width;
        } else {
            block.style.width = 'auto';
        }
    });
});

async function getTimelineList() {
    const {data} = await trackersCompaniesService.getTimelineList(trackerId, {filters: serverFilters});

    startups.value = data;
    startupsLoaded.value = true;
}

function timelineChanged() {
    getTimelineList();
    emit('timelineChanged');
}

function calculatePosition(startup) {
    let style = {
        width: 0
    };

    if (startup.start_date && startup.end_date) {
        let startupStartDate = new Date(startup.start_date + 'T00:00:00');

        if (startupStartDate < startDate.value) {
            startupStartDate = startDate.value;
        }

        const startupEndDate = new Date(startup.end_date);

        const dateDiff1 = Math.floor((startupStartDate - startDate.value) / (1000 * 60 * 60 * 24));
        const dateDiff2 = Math.floor((startupEndDate - startupStartDate) / (1000 * 60 * 60 * 24));

        style.marginLeft = 100 * dateDiff1 / timeline.value + '%';
        style.width = 100 * dateDiff2 / timeline.value + '%';
        style.paddingRight = 0;
    }

    return style;
}

function calculateSubPeriodPosition(startup, subPeriod) {
    let position = {
        width: 0
    };

    if (startup.start_date && startup.end_date && subPeriod[0] && subPeriod[1]) {
        let startupStartDate = new Date(startup.start_date)

        if (startupStartDate < startDate.value) {
            startupStartDate = startDate.value;
        }

        const startupEndDate = new Date(startup.end_date)

        const period = (startupEndDate - startupStartDate) / (1000 * 60 * 60 * 24);

        const periodStartDate = new Date(subPeriod[0])
        const periodEndDate = new Date(subPeriod[1])

        const dateDiff1 = Math.ceil((periodStartDate - startupStartDate) / (1000 * 60 * 60 * 24));
        const dateDiff2 = Math.ceil((periodEndDate - periodStartDate) / (1000 * 60 * 60 * 24));

        position.marginLeft = 'calc(100% / ' + period + ' * ' + dateDiff1 + ')';
        position.width = 'calc(100% / ' + period + ' * ' + dateDiff2 + ')';
    }

    return position;
}

function calculateSubPeriodMargin(startup, subPeriod) {
    let margin = 0;
    let position = calculateSubPeriodPosition(startup, subPeriod);

    if ('marginLeft' in position) {
        margin = position.marginLeft;
    }

    return margin;
}

function checkTestPeriod() {
    let leftSide = document.getElementsByClassName('status-page__aside');

    if (leftSide.length) {
        let leftSideOffsetBound = leftSide[0].getBoundingClientRect();
        let rightOffset = leftSideOffsetBound.x + leftSideOffsetBound.width;
        let blocks = document.getElementsByClassName('poc-block');

        for (let block of blocks) {
            let testBlock = block.getElementsByClassName('status-page__testing');

            if (testBlock.length) {
                testBlock = testBlock[0];
                let x =  testBlock.getBoundingClientRect().x;

                if (x < rightOffset) {
                    testBlock.classList.add('status-page__testing--in-past')
                } else {
                    testBlock.classList.remove('status-page__testing--in-past')
                }
            }
        }
    }
}

function calculateMonthsList() {
    startDate.value = null;
    endDate.value = null;

    startups.value.forEach(startup => {
        if (startup.start_date) {
            let firstMonthDate = firstDayOfMonth(new Date(startup.start_date));

            if (!startDate.value || firstMonthDate < startDate.value) {
                startDate.value = firstMonthDate
            }
        }

        if (startup.end_date) {
            let lastMonthDate = lastDayOfMonth(new Date(startup.end_date));

            if (!endDate.value || lastMonthDate > endDate.value) {
                endDate.value = lastMonthDate
            }
        }
    })

    if (!startDate.value) {
        startDate.value = firstDayOfMonth(new Date());
    }

    if (!endDate.value) {
        let newStartDate = lastDayOfMonth(new Date());
        endDate.value = new Date(newStartDate.setMonth(newStartDate.getMonth()+11));
    }

    let currentMonth = startDate.value.getMonth();
    let currentYear = startDate.value.getFullYear();
    let newMonths = [];
    let today = new Date();

    while (
        (currentYear <= endDate.value.getFullYear() && (currentYear < endDate.value.getFullYear() || currentMonth <= endDate.value.getMonth()))
        || newMonths.length < 12
        ) {

        newMonths.push({
            month: monthsList[currentMonth],
            year: currentYear,
            isCurrent: today.getFullYear() === currentYear && today.getMonth() === currentMonth
        });

        ++currentMonth;

        if (currentMonth === 12) {
            currentMonth = 0;
            currentYear++;
        }
    }

    if (endDate.value < new Date(currentYear, currentMonth, 1)) {
        if (currentMonth === 0) {
            currentMonth = 11;
            currentYear--;
        } else {
            currentMonth--;
        }
    }

    endDate.value = new Date(currentYear, currentMonth, 30);
    timeline.value = Math.ceil((endDate.value - startDate.value) / (1000 * 60 * 60 * 24)) + 1;
    months.value = newMonths;
}

function getBlockStyle(startup) {
    let style = {};

    if (startup.color && !isEmpty(startup)) {
        style.borderColor = startup.color;
        style.backgroundColor = startup.color;
    }

    if (startup.sub_periods.length) {
        style.padding = "0 !important";
    }

    return style;
}

function isEmpty(startup) {
    return !startup.end_date;
}

function getPlaceholder(startup) {
    return startup.status ? 'Click here to add timeline and notes' : 'Click here to add status, timeline and notes';
}
</script>

<template>
    <div class="status-page">
        <div v-if="startups.length" class="status-page__content">
            <!-- <aside class="status-page__aside"> -->
                <!-- <header class="status-page__header"/> -->

                <!--ul class="status-page__list">
                    <li v-for="(startup, index) in startups" :key="index">
                        <div class="status-page__list__content">
                            <div class="image-block image-block--22">
                                <img :src="getLogo(startup.logoUrl)" alt="img"/>
                            </div>

                            <a :href="'/startups/' + startup.id" target="_blank">
                                <h5 class="heading5">
                                    <b-button v-b-modal.poc-modal><span class="line-clamp line-clamp1">{{startup.name}}</span></b-button>
                                </h5>
                            </a>
                        </div>

                        <footer v-if="startup.sourced_by" class="status-page__list__footer">
                            <p v-if="startup.department" class="status-page__list__department line-clamp line-clamp1">{{startup.department}}</p>
                            <p class="line-clamp line-clamp1">Sourced by {{startup.sourced_by}}</p>
                        </footer>
                    </li>
                </ul-->
            <!-- </aside> -->

            <section class="status-page__data" id="board-wrapper" @scroll="checkTestPeriod">
                <div class="status-page__data__wrapper" :style="timelineWidth">
                    <header class="status-page__header">
                        <ul v-if="months.length" class="status-page__calendar" style="position: relative">
                            <li
                                v-for="month in months"
                                :key="month.year+'-'+month.month"
                                :class="{'current-month': month.isCurrent}"
                                ref="monthRefs"
                            >
                                {{month.month}}
                                <span class="status-page__calendar__year" v-if="month.month === 'Jan'">
                                    <sup>
                                        {{month.year}}
                                    </sup>
                                </span>
                            </li>
                        </ul>
                    </header>

                    <!--<div v-if="startups.length" class="status-page__data__inner" :style="{backgroundSize: 'calc(100% / ' + this.months.length + ') calc(100% / ' + this.months.length + ')'}">-->
                    <div v-if="startups.length" class="status-page__data__inner" ref="startupsInnerBlock">
                        <div v-for="(startup, index) in startups"
                             class="status-page__data__inner__flex" 
                             :key="index">

                            <div class="status-page__list__content-wrapper">
                                <div class="status-page__list__content-wrapper__inner">
                                    <div class="status-page__list__content">
                                        <div class="image-block image-block--22">
                                            <img :src="getLogo(startup.logoUrl)" alt="img"/>
                                        </div>

                                        <a v-if="!store.getters.onlyNeeds" :href="'/startups/' + startup.id" target="_blank">
                                            <h5 class="heading5 status-page__list__content__button">
                                                <b-button v-b-modal.poc-modal><span class="line-clamp line-clamp1">{{startup.name}}</span></b-button>
                                            </h5>
                                        </a>
                                        <h5 v-else class="heading5 status-page__list__content__button">
                                            <b-button v-b-modal.poc-modal><span class="line-clamp line-clamp1">{{startup.name}}</span></b-button>
                                        </h5>
                                    </div>

                                    <footer v-if="startup.sourced_by" class="status-page__list__footer">
                                        <p v-if="startup.department" class="status-page__list__department line-clamp line-clamp1">{{startup.department}}</p>
                                        <p class="line-clamp line-clamp1">Sourced by {{startup.sourced_by}}</p>
                                    </footer>
                                </div>
                            </div>

                            <div class="status-page__right" style="flex: 1; display: flex; align-items: center;">
                                <div :style="calculatePosition(startup)" class="align-self-end">
                                    

                                    <div class="poc-block position-relative" :class="{'poc-block--add': !startup.end_date}">
                                        <template v-if="startup.status && (startup.sub_periods.length || !startup.end_date)">
                                            <h6 class="heading6 status-page__data__title line-clamp line-clamp1">
                                                <span class="Atext-nowrap">{{startup.status}} </span>
                                                <span :title="startup.notes" class="Aline-clamp Aline-clamp1 status-page__data__notes"> {{startup.notes}}</span>
                                            </h6>
                                        </template>

                                        

                                        <div class="poc-block__inner"
                                             :style="getBlockStyle(startup)"
                                             :class="{'started-in-past': new Date(startup.start_date) < startDate}"
                                             @click="timelineEditModal.open(startup.id)"
                                        >
                                            <div
                                                v-if="startup.test_start_date"
                                                class="status-page__testing"
                                                :class="{'yellow-timeline': $store.state.user.company_id === 290485}"
                                                :style="calculateSubPeriodPosition(startup, [startup.test_start_date, startup.test_end_date])"
                                            />
                                            <div v-if="startup.sub_periods.length" class="mt-2">
                                                <div v-for="period in startup.sub_periods" :key="period.id" class="poc-block__item">
                                                    <div>
                                                        <div class="position-relative status-page__testing" style="background-color: white" :style="calculateSubPeriodPosition(startup, period.value)"/>
                                                        <span :title="period.name" class="line-clamp line-clamp1" :style="{marginLeft: calculateSubPeriodMargin(startup, period.value)}">{{period.name}}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="poc-block__inner__inside">
                                                <template v-if="isEmpty(startup)">
                                                    <h6 class="heading6">Fill in data for this startup</h6>
                                                    <p>{{getPlaceholder(startup)}}</p>
                                                </template>

                                                <template v-else-if="!startup.sub_periods.length">
                                                    <h6 class="heading6 line-clamp line-clamp1">{{startup.status}}</h6>
                                                    <p class="poc-block__inner__text line-clamp line-clamp1">{{startup.notes}}</p>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>

        <div v-if="startupsLoaded && !startups.length && tracker.user_permissions.includes('tracker-startups-add')" class="start-startup">
            <p>Add startups to your tracker to get started</p>

            <button @click="addStartups" class="btn btn-primary btn-lg">+ Add Startups</button>
        </div>

        <TimelineEdit
            ref="timelineEditModal"
            :tracker-id="trackerId"
            :permissions="tracker.user_permissions"
            @timelineChanged="timelineChanged"
            @removeStartup="removeStartup"
        />
    </div>
</template>

<style scoped>
    
</style>
